import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type {
  ApprovalBizTypeRow,
  ApprovalDelegateRow,
  ApprovalFlowAuthRow,
  ApprovalFlowRow,
  ApprovalFlowSaveRequest,
  ApprovalVariableRow
} from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import {
  APPROVAL_NODE_TYPE_OPTIONS,
  APPROVAL_SCOPE_OPTIONS,
  approvalFlowStatusMeta,
  formatDisplayTime,
  LEVEL_LABELS
} from '@hq/utils';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import type { HqApi } from '@hq/api-sdk';
import { ellipsisCell, TipText, sumWidth } from '@hq/ui-kit';

/** 账号等级（`sys_account_level.level_code`）：手填拼错会让审批人解析/上抛静默失效。 */
const LEVEL_OPTIONS = Object.entries(LEVEL_LABELS).map(([code, text]) => ({ value: code, label: text }));

/**
 * 指定人员多选。手填 userId 既猜不到是谁，写错又只会让节点解析出一个不存在的审批人，
 * 改为按姓名/工号搜索（`/wl/approval/user-search`，登录即可读）。
 * nameMap 用详情带回来的 userName 兜底，避免编辑态选中项只剩一串数字。
 */
function UserMultiPicker({
  hq,
  value,
  onChange,
  nameMap
}: {
  hq: HqApi;
  value?: string[];
  onChange?: (value?: string[]) => void;
  nameMap?: Record<string, string>;
}) {
  const [keyword, setKeyword] = useState('');
  const users = useQuery({
    queryKey: ['approval', 'user-search', keyword],
    staleTime: 30_000,
    queryFn: () => hq.approval.searchUsers(keyword || undefined, 50)
  });
  const options = new Map<string, string>(
    (users.data ?? []).map((row) => [row.id, `${row.name ?? '未命名'}（${row.userCode ?? row.id}）`] as const)
  );
  (value ?? []).forEach((id) => {
    const known = nameMap?.[id];
    if (!options.has(id)) options.set(id, known ? `${known}（${id}）` : id);
  });
  return (
    <Select
      mode="multiple"
      allowClear
      showSearch
      optionFilterProp="label"
      maxTagCount="responsive"
      placeholder="搜索姓名 / 工号"
      value={value}
      onSearch={setKeyword}
      onChange={(next) => onChange?.(next)}
      loading={users.isFetching}
      options={[...options].map(([id, label]) => ({ value: id, label }))}
    />
  );
}

// 状态/节点类型/审批人来源统一由 `@hq/utils` 提供：hospital 端曾各有一份副本，
// 同一业务对象两端配色与可选项不一致（DRAFT 一端 gold 一端 default）。
const NODE_TYPES = APPROVAL_NODE_TYPE_OPTIONS;

const SCOPES = APPROVAL_SCOPE_OPTIONS;

const TIMEOUT_ACTIONS = [
  { value: 'REMIND', label: '催办提醒' },
  { value: 'ESCALATE', label: '上抛升级' },
  { value: 'TRANSFER_ADMIN', label: '转办管理员' },
  { value: 'AUTO_PASS', label: '自动通过' },
  { value: 'AUTO_REJECT', label: '自动驳回' }
];

/** 需要 resolve_rule_json 的动态审批人来源：占位示例直接给出可用写法。 */
const DYNAMIC_SCOPE_PLACEHOLDERS: Record<string, string> = {
  DYNAMIC: '{"orgLevelDelta":1,"targetScope":"ORG_ADMIN"}',
  EXPRESSION:
    '{"branches":[{"condition":{"amount":{"op":"gte","value":50000}},"roleCode":"PLATFORM_ADMIN"},{"condition":null,"level":"L3"}]}',
  AI_ASSIGN: '{"historyWindowDays":180,"topN":3}'
};

const DYNAMIC_SCOPES = Object.keys(DYNAMIC_SCOPE_PLACEHOLDERS);

/** 条件 JSON 前端先校验，避免错误要等后端 ApprovalFlowAdmin.EnsureJson 才暴露。 */
const jsonRule = (label: string) => ({
  validator: (_rule: unknown, value?: string) => {
    const text = (value ?? '').trim();
    if (!text) return Promise.resolve();
    try {
      JSON.parse(text);
      return Promise.resolve();
    } catch {
      return Promise.reject(new Error(`${label}不是合法 JSON`));
    }
  }
});

const AUTH_TYPES = [
  { value: 'START', label: '发起' },
  { value: 'MANAGE', label: '管理' },
  { value: 'VIEW_LOG', label: '查看轨迹' },
  { value: 'INTERVENE', label: '干预' }
];

const AUTH_TARGET_TYPES = [
  { value: 'ROLE', label: '角色' },
  { value: 'USER', label: '用户' },
  { value: 'LEVEL', label: '等级' },
  { value: 'DEPT', label: '科室' }
];

interface NodeFormValues {
  nodeCode: string;
  nodeName: string;
  nodeType: string;
  seq?: number;
  approveScope: string;
  /** 表单里是多选数组，提交时 join(',') 还原后端的逗号分隔串。 */
  roleCode?: string[];
  levelRequired?: string;
  /** DYNAMIC / EXPRESSION / AI_ASSIGN 的审批人解析规则（JSON 文本）。 */
  resolveRuleJson?: string;
  signRate?: number;
  counterSignOrder?: string;
  timeoutHours?: number;
  timeoutAction?: string;
  escalateLevelCode?: string;
  userIds?: string[];
}

interface RouteFormValues {
  conditionJson?: string;
  targetNodeCode: string;
  priority?: number;
  description?: string;
}

interface FlowFormValues {
  flowCode: string;
  flowName: string;
  bizType: string;
  orgId?: string;
  applicableScope?: string;
  allowTransfer?: boolean;
  allowAddSigner?: boolean;
  allowRejectTo?: boolean;
  supportBatchApprove?: boolean;
  startConditionJson?: string;
  remark?: string;
  nodes: NodeFormValues[];
  routes?: RouteFormValues[];
}

interface AuthFormValues {
  authType: string;
  targetType: string;
  /** 多选数组，提交时 join(',')（后端 targetCodes 是逗号分隔串）。 */
  targetCodes: string[];
  scopeOrgId?: string;
}

/** 审批流配置：流程/节点/条件路由，以及业务注册、路由变量、委托代理只读视图与独立授权配置。 */
export function ApprovalFlowsPage({ hq, platform = false }: { hq: HqApi; platform?: boolean }) {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();

  const [keyword, setKeyword] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingStatus, setEditingStatus] = useState<string>('DRAFT');
  const [form] = Form.useForm<FlowFormValues>();
  const [authForm] = Form.useForm<AuthFormValues>();
  const [authFlowId, setAuthFlowId] = useState<string | null>(null);
  /** 独立授权的对象类型决定「目标」用哪个选择器。 */
  const authTargetType = Form.useWatch('targetType', authForm) ?? 'ROLE';
  // 编辑审批流时把详情里的 userId → userName 暂存，给人选下拉做回显
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  // 条件路由的目标节点只能从本流程已配置的节点里选（手填拼错后端会 422）
  const nodeValues = Form.useWatch('nodes', form) as NodeFormValues[] | undefined;
  const nodeOptions = (nodeValues ?? [])
    .filter((node) => Boolean(node?.nodeCode))
    .map((node) => ({ value: String(node.nodeCode), label: `${node.nodeName || node.nodeCode}（${node.nodeCode}）` }));

  const flows = useQuery({
    queryKey: ['sys', 'approval', 'flows', keyword],
    placeholderData: keepPreviousData,
    queryFn: () => hq.approvalFlow.flow.list({ keyword: keyword || undefined })
  });

  const bizTypes = useQuery({
    queryKey: ['sys', 'approval', 'biz-types'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.approvalFlow.bizTypes()
  });

  // 机构名映射：流程表只存 org_id，列表直接展示 ID 时用户无法辨认。
  // 用 options 而不是 list：配审批流的人未必有 sys:org:manage，list 会 403。
  const orgs = useQuery({
    queryKey: ['sys', 'approval', 'org-options'],
    staleTime: 5 * 60 * 1000,
    queryFn: () => hq.org.options()
  });

  // 角色编码走主数据下拉：手填 role_code 拼错只会让节点解析不出审批人
  const roles = useQuery({
    queryKey: ['sys', 'approval', 'role-options'],
    staleTime: 5 * 60 * 1000,
    queryFn: () => hq.admin.roles.list()
  });
  const roleOptions = (roles.data ?? []).map((row) => ({
    value: row.roleCode ?? row.id,
    label: row.roleName ?? row.roleCode ?? row.id
  }));

  // 独立授权的「科室」目标同样需要主数据
  const authDepts = useQuery({
    queryKey: ['sys', 'approval', 'dept-options'],
    staleTime: 5 * 60 * 1000,
    queryFn: () => hq.orgStructure.dept.list({}),
    enabled: authFlowId !== null
  });

  const orgNames = new Map((orgs.data ?? []).map((row) => [row.id, row.orgName] as const));
  const bizTypeNames = new Map((bizTypes.data ?? []).map((row) => [row.bizType, row.bizName]));

  const variables = useQuery({
    queryKey: ['sys', 'approval', 'variables'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.approvalFlow.variables.list()
  });

  const delegates = useQuery({
    queryKey: ['sys', 'approval', 'delegates'],
    placeholderData: keepPreviousData,
    queryFn: () => hq.approvalFlow.delegates.list()
  });

  const flowAuths = useQuery({
    queryKey: ['sys', 'approval', 'flow-auth', authFlowId],
    enabled: Boolean(authFlowId),
    queryFn: () => hq.approvalFlow.auth.list(authFlowId as string)
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['sys', 'approval'] });
  };

  const save = useMutation({
    mutationFn: async (values: FlowFormValues) => {
      const body: ApprovalFlowSaveRequest = {
        flowCode: values.flowCode.trim(),
        flowName: values.flowName.trim(),
        bizType: values.bizType,
        orgId: values.orgId || undefined,
        applicableScope: values.applicableScope,
        allowTransfer: values.allowTransfer ?? true,
        allowAddSigner: values.allowAddSigner ?? true,
        allowRejectTo: values.allowRejectTo ?? true,
        supportBatchApprove: values.supportBatchApprove ?? false,
        startConditionJson: values.startConditionJson?.trim() || undefined,
        remark: values.remark,
        nodes: (values.nodes ?? []).map((node) => {
          const userIds = node.userIds ?? [];
          return {
            nodeCode: node.nodeCode,
            nodeName: node.nodeName,
            nodeType: node.nodeType,
            seq: node.seq,
            approveScope: node.approveScope,
            roleCode: (node.roleCode ?? []).map((code) => code.trim()).filter(Boolean).join(',') || undefined,
            levelRequired: node.levelRequired,
            resolveRuleJson: node.resolveRuleJson?.trim() || undefined,
            signRate: node.signRate ?? 100,
            allowDelegate: true,
            counterSignOrder: node.counterSignOrder ?? 'SERIAL',
            timeoutHours: node.timeoutHours ?? 0,
            timeoutAction: node.timeoutAction ?? 'REMIND',
            escalateLevelCode: node.escalateLevelCode,
            users: userIds.map((userId) => ({ userId }))
          };
        }),
        routes: (values.routes ?? [])
          .filter((route) => route.targetNodeCode)
          .map((route) => ({
            conditionJson: route.conditionJson?.trim() || undefined,
            targetNodeCode: route.targetNodeCode,
            priority: route.priority ?? 0,
            description: route.description
          }))
      };

      // 已发布/已停用版本的修改由后端另存为新草稿版本
      return editingId ? hq.approvalFlow.flow.update(editingId, body) : hq.approvalFlow.flow.create(body);
    },
    onSuccess: async (result) => {
      message.success(
        result.id !== editingId ? `已保存为新草稿（v${result.version}，ID ${result.id}）` : `已保存草稿（v${result.version}）`
      );
      setEditorOpen(false);
      setEditingId(null);
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const publish = useMutation({
    mutationFn: (id: string) => hq.approvalFlow.flow.publish(id),
    onSuccess: async () => {
      message.success('审批流已发布，历史单据继续沿用旧版本');
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '发布失败')
  });

  const disable = useMutation({
    mutationFn: (id: string) => hq.approvalFlow.flow.disable(id),
    onSuccess: async () => {
      message.success('审批流已停用');
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '停用失败')
  });

  const remove = useMutation({
    mutationFn: (id: string) => hq.approvalFlow.flow.remove(id),
    onSuccess: async () => {
      message.success('审批流已删除');
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '删除失败')
  });

  const saveAuth = useMutation({
    mutationFn: (values: AuthFormValues) =>
      hq.approvalFlow.auth.save(authFlowId as string, {
        authType: values.authType,
        targetType: values.targetType,
        targetCodes: values.targetCodes.map((code) => code.trim()).filter(Boolean).join(','),
        scopeOrgId: values.scopeOrgId || '0'
      }),
    onSuccess: async () => {
      message.success('授权已添加');
      authForm.resetFields();
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '添加授权失败')
  });

  const toggleAuth = useMutation({
    mutationFn: (row: ApprovalFlowAuthRow) =>
      hq.approvalFlow.auth.update(row.id, {
        authType: row.authType,
        targetType: row.targetType,
        targetCodes: row.targetCodes,
        scopeOrgId: row.scopeOrgId,
        effectiveFrom: row.effectiveFrom ?? undefined,
        effectiveTo: row.effectiveTo ?? undefined,
        status: row.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE'
      }),
    onSuccess: async () => {
      message.success('授权状态已更新');
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '更新授权失败')
  });

  const removeAuth = useMutation({
    mutationFn: (id: string) => hq.approvalFlow.auth.remove(id),
    onSuccess: async () => {
      message.success('授权已删除');
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '删除授权失败')
  });

  const openCreate = () => {
    setEditingId(null);
    setEditingStatus('DRAFT');
    form.resetFields();
    form.setFieldsValue({
      orgId: '1',
      applicableScope: 'PLATFORM',
      allowTransfer: true,
      allowAddSigner: true,
      allowRejectTo: true,
      nodes: [
        {
          nodeCode: 'N1',
          nodeName: '科室审批',
          nodeType: 'APPROVE',
          seq: 10,
          approveScope: 'DEPT_HEAD',
          signRate: 100,
          timeoutHours: 24,
          timeoutAction: 'REMIND'
        }
      ],
      routes: []
    });
    setEditorOpen(true);
  };

  const openEdit = async (row: ApprovalFlowRow) => {
    const detail = await hq.approvalFlow.flow.detail(row.id).catch((error: unknown) => {
      message.error(error instanceof ApiError ? error.friendlyMessage : '审批流详情加载失败');
      return null;
    });
    if (!detail) return;
    // 先缓存一遍人名：下拉候选是搜出来的，已选但不在搜索结果里的 id 靠它回显
    const names: Record<string, string> = {};
    detail.nodes.forEach((node) =>
      node.users.forEach((item) => {
        if (item.userName) names[item.userId] = item.userName;
      })
    );
    setUserNames(names);
    setEditingId(row.id);
    setEditingStatus(row.status);
    form.resetFields();
    form.setFieldsValue({
      flowCode: detail.flow.flowCode,
      flowName: detail.flow.flowName,
      bizType: detail.flow.bizType,
      orgId: detail.flow.orgId,
      applicableScope: detail.flow.applicableScope,
      allowTransfer: detail.flow.allowTransfer,
      allowAddSigner: detail.flow.allowAddSigner,
      allowRejectTo: detail.flow.allowRejectTo,
      supportBatchApprove: detail.flow.supportBatchApprove,
      startConditionJson: detail.flow.startConditionJson ?? undefined,
      remark: detail.flow.remark ?? undefined,
      nodes: detail.nodes.map((node) => ({
        nodeCode: node.nodeCode,
        nodeName: node.nodeName,
        nodeType: node.nodeType,
        seq: node.seq,
        approveScope: node.approveScope,
        roleCode: node.roleCode ? node.roleCode.split(',').map((code) => code.trim()).filter(Boolean) : undefined,
        levelRequired: node.levelRequired ?? undefined,
        resolveRuleJson: node.resolveRuleJson ?? undefined,
        signRate: node.signRate,
        counterSignOrder: node.counterSignOrder,
        timeoutHours: node.timeoutHours,
        timeoutAction: node.timeoutAction ?? 'REMIND',
        escalateLevelCode: node.escalateLevelCode ?? undefined,
        userIds: node.users
          .filter((item) => item.userId !== '0')
          .map((item) => item.userId)
      })),
      routes: detail.routes.map((route) => ({
        conditionJson: route.conditionJson ?? undefined,
        targetNodeCode: route.targetNodeCode,
        priority: route.priority,
        description: route.description ?? undefined
      }))
    });
    setEditorOpen(true);
  };

  const columns: ColumnsType<ApprovalFlowRow> = [
    { title: '流程编码', dataIndex: 'flowCode', width: 190 },
    { title: '名称', dataIndex: 'flowName', width: 200 },
    {
      title: '业务类型',
      dataIndex: 'bizType',
      width: 170,
      render: (value: string) => {
        const name = bizTypeNames.get(value);
        return name ? `${name}（${value}）` : value;
      }
    },
    // 平台视角：区分平台模板与具体租户的流程，避免误改别人的流程
    ...(platform
      ? [
          {
            title: '来源',
            dataIndex: 'tenantId',
            width: 150,
            render: (value: string) => (value === '0' ? <Tag color="blue">平台模板</Tag> : <Tag>租户 #{value}</Tag>)
          } as ColumnsType<ApprovalFlowRow>[number]
        ]
      : []),
    {
      title: '机构',
      dataIndex: 'orgId',
      width: 200,
      ellipsis: { showTitle: false },
      render: (value: string) => <TipText text={value === '0' ? '平台模板' : (orgNames.get(value) ?? `机构 ${value}`)} />
    },
    { title: '版本', dataIndex: 'version', width: 80, render: (value: number) => `v${value}` },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: string) => {
        const meta = approvalFlowStatusMeta(value);
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '更新时间', dataIndex: 'updateAt', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      key: 'action',
      width: 280,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Space>
          <Button type="link" onClick={() => void openEdit(row)}>
            编辑
          </Button>
          {row.status === 'DRAFT' ? (
            <Button type="link" onClick={() => publish.mutate(row.id)} loading={publish.isPending}>
              发布
            </Button>
          ) : null}
          {row.status === 'PUBLISHED' ? (
            <Button
              type="link"
              onClick={() =>
                modal.confirm({
                  title: `停用审批流 ${row.flowCode}？`,
                  content: '停用后新单据将无法匹配该流程，已发起单据不受影响。',
                  okText: '停用',
                  cancelText: '取消',
                  onOk: () => disable.mutateAsync(row.id)
                })
              }
            >
              停用
            </Button>
          ) : null}
          {row.status !== 'PUBLISHED' ? (
            <Button
              type="link"
              danger
              onClick={() =>
                modal.confirm({
                  title: `删除审批流 ${row.flowCode} v${row.version}？`,
                  okText: '删除',
                  cancelText: '取消',
                  onOk: () => remove.mutateAsync(row.id)
                })
              }
            >
              删除
            </Button>
          ) : null}
        </Space>
      )
    }
  ];

  const delegateColumns: ColumnsType<ApprovalDelegateRow> = [
    { title: '委托人', dataIndex: 'principalName', width: 160, render: (value: string | null, row) => value ?? row.principalUserId },
    { title: '代理人', dataIndex: 'agentName', width: 160, render: (value: string | null, row) => value ?? row.agentUserId },
    { title: '类型', dataIndex: 'delegateType', width: 110 },
    { title: '自动转发', dataIndex: 'autoForward', width: 100, render: (value: boolean) => (value ? '是' : '否') },
    { title: '开始', dataIndex: 'beginAt', width: 160, render: (value: string) => formatDisplayTime(value) },
    { title: '结束', dataIndex: 'endAt', width: 160, render: (value: string) => formatDisplayTime(value) },
    { title: '状态', dataIndex: 'status', width: 110 },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_value: unknown, row) =>
        row.status === 'EFFECTIVE' ? (
          <Button
            type="link"
            danger
            onClick={() => void hq.approvalFlow.delegates.revoke(row.id).then(() => invalidate())}
          >
            撤销
          </Button>
        ) : null
    }
  ];

  const variableColumns: ColumnsType<ApprovalVariableRow> = [
    { title: '变量码', dataIndex: 'varCode', width: 160 },
    { title: '名称', dataIndex: 'varName', width: 140 },
    { title: '类型', dataIndex: 'varType', width: 100 },
    { title: '取值方式', dataIndex: 'sourceType', width: 120 },
    { title: '取值表达式', dataIndex: 'sourceExpr', ellipsis: { showTitle: false }, render: ellipsisCell('sourceExpr') },
    { title: '适用业务', dataIndex: 'bizTypes', width: 160, render: (value: string | null) => value || '通用' }
  ];

  const authColumns: ColumnsType<ApprovalFlowAuthRow> = [
    {
      title: '授权类型',
      dataIndex: 'authType',
      width: 120,
      render: (value: string) => AUTH_TYPES.find((item) => item.value === value)?.label ?? value
    },
    {
      title: '目标类型',
      dataIndex: 'targetType',
      width: 120,
      render: (value: string) => AUTH_TARGET_TYPES.find((item) => item.value === value)?.label ?? value
    },
    { title: '目标编码', dataIndex: 'targetCodes', ellipsis: { showTitle: false }, render: ellipsisCell('targetCodes') },
    {
      title: '生效机构',
      dataIndex: 'scopeOrgId',
      width: 200,
      ellipsis: { showTitle: false },
      render: (value: string) => <TipText text={value === '0' ? '全区' : (orgNames.get(value) ?? `机构 ${value}`)} />
    },
    {
      title: '生效期',
      key: 'period',
      width: 320,
      render: (_value: unknown, row) => `${formatDisplayTime(row.effectiveFrom)} ~ ${formatDisplayTime(row.effectiveTo)}`
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: string) => (value === 'ACTIVE' ? <Tag color="green">生效中</Tag> : <Tag>已停用</Tag>)
    },
    {
      title: '操作',
      key: 'action',
      width: 170,
      fixed: 'right',
      render: (_value: unknown, row) => (
        <Space>
          <Button type="link" loading={toggleAuth.isPending} onClick={() => toggleAuth.mutate(row)}>
            {row.status === 'ACTIVE' ? '停用' : '启用'}
          </Button>
          <Button
            type="link"
            danger
            onClick={() =>
              modal.confirm({
                title: `删除该${AUTH_TYPES.find((item) => item.value === row.authType)?.label ?? row.authType}授权？`,
                content: `目标：${row.targetType} ${row.targetCodes}。删除后若该类型无其他授权，将恢复为默认放行。`,
                okText: '删除',
                cancelText: '取消',
                onOk: () => removeAuth.mutateAsync(row.id)
              })
            }
          >
            删除
          </Button>
        </Space>
      )
    }
  ];

  const bizTypeColumns: ColumnsType<ApprovalBizTypeRow> = [
    { title: '业务类型', dataIndex: 'bizType', width: 150 },
    { title: '名称', dataIndex: 'bizName', width: 180 },
    { title: '业务域', dataIndex: 'bizDomain', width: 120 },
    { title: '主表 / 状态字段', key: 'table', width: 220, render: (_v, row) => `${row.mainTable ?? '-'} / ${row.statusField ?? '-'}` },
    { title: '绑定模式', dataIndex: 'flowBindingMode', width: 110 },
    { title: '启用', dataIndex: 'enabled', width: 80, render: (value: boolean) => (value ? '是' : '否') }
  ];

  return (
    <Card
      className="page-card"
      title="审批流配置"
      extra={
        <Space>
          <Input.Search
            placeholder="流程编码 / 名称"
            allowClear
            style={{ width: 240 }}
            onSearch={(value) => setKeyword(value)}
          />
          <Button icon={<ReloadOutlined />} onClick={() => void invalidate()} loading={flows.isFetching}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            新建流程
          </Button>
        </Space>
      }
    >
      {flows.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="审批流加载失败"
          description={flows.error instanceof ApiError ? flows.error.friendlyMessage : String(flows.error)}
        />
      ) : null}

      <Tabs
        items={[
          {
            key: 'flows',
            label: '流程定义',
            children: (
              <Table<ApprovalFlowRow>
                rowKey="id"
                size="middle"
                columns={columns}
                dataSource={flows.data ?? []}
                loading={flows.isPending}
                pagination={false}
                scroll={{ x: sumWidth(columns) }}
                locale={{ emptyText: '暂无审批流，点击「新建流程」或执行 db/wz-conf/04-cfg-approval-seed.sql' }}
              />
            )
          },
          {
            key: 'biz-types',
            label: '业务注册',
            children: (
              <>
                <Typography.Paragraph type="secondary">
                  新增业务线只需在 cfg_biz_registry 注册（biz_type / 主表 / 状态字段），审批引擎按注册信息回写单据状态。
                </Typography.Paragraph>
                <Table<ApprovalBizTypeRow>
                  rowKey="bizType"
                  size="middle"
                  columns={bizTypeColumns}
                  dataSource={bizTypes.data ?? []}
                  loading={bizTypes.isPending}
                  pagination={false}
                  locale={{ emptyText: '暂无注册业务' }}
                />
              </>
            )
          },
          {
            key: 'variables',
            label: '路由变量',
            children: (
              <Table<ApprovalVariableRow>
                rowKey="id"
                size="middle"
                columns={variableColumns}
                dataSource={variables.data ?? []}
                loading={variables.isPending}
                pagination={false}
                locale={{ emptyText: '暂无变量（可执行 04-cfg-approval-seed.sql 初始化）' }}
              />
            )
          },
          {
            key: 'delegates',
            label: '委托代理',
            children: (
              <Table<ApprovalDelegateRow>
                rowKey="id"
                size="middle"
                pagination={false}
                loading={delegates.isPending}
                dataSource={delegates.data ?? []}
                scroll={{ x: sumWidth(delegateColumns) }}
                locale={{ emptyText: '暂无委托代理' }}
                columns={delegateColumns}
              />
            )
          },
          {
            key: 'auth',
            label: '授权配置',
            children: (
              <>
                <Typography.Paragraph type="secondary">
                  审批流授权独立于角色权限码：配置后只有命中的角色 / 用户 / 等级 / 科室可执行对应操作；
                  某类授权未配置时保持默认放行（向后兼容）。
                </Typography.Paragraph>
                <Space wrap style={{ marginBottom: 12 }}>
                  <Select
                    style={{ width: 380 }}
                    placeholder="选择审批流"
                    showSearch
                    optionFilterProp="label"
                    value={authFlowId ?? undefined}
                    onChange={(value: string) => setAuthFlowId(value)}
                    options={(flows.data ?? []).map((item) => ({
                      value: item.id,
                      label: `${item.flowCode} v${item.version} · ${item.flowName}`
                    }))}
                  />
                  <Button
                    icon={<ReloadOutlined />}
                    disabled={!authFlowId}
                    loading={flowAuths.isFetching}
                    onClick={() => void flowAuths.refetch()}
                  >
                    刷新授权
                  </Button>
                </Space>
                {authFlowId ? (
                  <>
                    <Form<AuthFormValues>
                      form={authForm}
                      layout="inline"
                      style={{ marginBottom: 12, rowGap: 8 }}
                      onFinish={(values) => saveAuth.mutate(values)}
                    >
                      <Form.Item name="authType" rules={[{ required: true, message: '请选择授权类型' }]}>
                        <Select style={{ width: 150 }} placeholder="授权类型" options={AUTH_TYPES} />
                      </Form.Item>
                      <Form.Item name="targetType" rules={[{ required: true, message: '请选择目标类型' }]}>
                        <Select style={{ width: 130 }} placeholder="目标类型" options={AUTH_TARGET_TYPES} />
                      </Form.Item>
                      <Form.Item
                        name="targetCodes"
                        rules={[{ required: true, message: '请选择授权对象' }]}
                        tooltip="按目标类型从主数据里选，多个可多选"
                      >
                        {authTargetType === 'USER' ? (
                          <UserMultiPicker hq={hq} nameMap={{}} />
                        ) : (
                          <Select
                            mode="multiple"
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            style={{ width: 300 }}
                            placeholder={
                              authTargetType === 'ROLE'
                                ? '角色'
                                : authTargetType === 'LEVEL'
                                  ? '账号等级'
                                  : '科室'
                            }
                            options={
                              authTargetType === 'ROLE'
                                ? roleOptions
                                : authTargetType === 'LEVEL'
                                  ? LEVEL_OPTIONS
                                  : (authDepts.data ?? []).map((row) => ({ value: row.id, label: row.deptName }))
                            }
                          />
                        )}
                      </Form.Item>
                      <Form.Item name="scopeOrgId" tooltip="0 = 全区生效，也可指定到单个机构">
                        <Select
                          showSearch
                          optionFilterProp="label"
                          style={{ width: 260 }}
                          placeholder="生效机构（0=全区）"
                          options={[
                            { value: '0', label: '全区（0）' },
                            ...(orgs.data ?? []).map((org) => ({ value: String(org.id), label: `${org.orgName}（${org.orgCode}）` }))
                          ]}
                        />
                      </Form.Item>
                      <Button type="primary" htmlType="submit" loading={saveAuth.isPending}>
                        添加授权
                      </Button>
                    </Form>
                    <Table<ApprovalFlowAuthRow>
                      rowKey="id"
                      size="middle"
                      pagination={false}
                      loading={Boolean(authFlowId) && flowAuths.isPending}
                      dataSource={flowAuths.data ?? []}
                      scroll={{ x: sumWidth(authColumns) }}
                      columns={authColumns}
                      locale={{ emptyText: '该流程暂无独立授权（默认放行）' }}
                    />
                  </>
                ) : (
                  <Alert type="info" showIcon message="请选择审批流后配置授权" />
                )}
              </>
            )
          }
        ]}
      />

      <Drawer
        title={editingId ? '编辑审批流' : '新建审批流'}
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        width={920}
        destroyOnHidden
        extra={
          <Space>
            <Button onClick={() => setEditorOpen(false)}>取消</Button>
            <Button type="primary" loading={save.isPending} onClick={() => void form.submit()}>
              保存草稿
            </Button>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={
            editingStatus === 'PUBLISHED'
              ? '当前为已发布版本，保存会生成新草稿版本；发布前旧版本继续生效。'
              : '草稿保存后需发布才会被审批引擎使用。'
          }
        />
        <Form<FlowFormValues> form={form} layout="vertical" onFinish={(values) => save.mutate(values)}>
          <Space size={12} style={{ display: 'flex', flexWrap: 'wrap' }}>
            <Form.Item name="flowCode" label="流程编码" rules={[{ required: true, message: '必填' }]} style={{ width: 220 }}>
              <Input placeholder="WL_APPLY_STD" />
            </Form.Item>
            <Form.Item name="flowName" label="流程名称" rules={[{ required: true, message: '必填' }]} style={{ width: 260 }}>
              <Input placeholder="申领单标准审批流" />
            </Form.Item>
            <Form.Item name="bizType" label="业务类型" rules={[{ required: true, message: '必填' }]} style={{ width: 200 }}>
              <Select
                options={(bizTypes.data ?? []).map((item) => ({ value: item.bizType, label: `${item.bizType} · ${item.bizName}` }))}
                placeholder="WL_APPLY"
              />
            </Form.Item>
            <Form.Item name="orgId" label="所属机构" style={{ width: 280 }} tooltip="选平台模板（所有机构通用）或具体机构；不确定的时候选平台模板">
              <Select
                showSearch
                optionFilterProp="label"
                placeholder="搜索并选择机构"
                options={[
                  { value: '0', label: '平台模板（0，所有机构通用）' },
                  ...(orgs.data ?? []).map((org) => ({ value: String(org.id), label: `${org.orgName}（${org.orgCode}）` }))
                ]}
              />
            </Form.Item>
          </Space>

          <Space size={24} wrap>
            <Form.Item name="applicableScope" label="适用范围" style={{ width: 160 }}>
              <Select
                options={['PLATFORM', 'GROUP', 'ORG', 'DEPT'].map((value) => ({ value, label: value }))}
              />
            </Form.Item>
            <Form.Item name="allowTransfer" label="允许转办" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="allowAddSigner" label="允许加签" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="allowRejectTo" label="允许退回节点" valuePropName="checked">
              <Switch />
            </Form.Item>
            <Form.Item name="supportBatchApprove" label="支持批量审批" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Space>

          <Form.Item name="startConditionJson" label="发起条件（JSON，可空）" rules={[jsonRule('发起条件')]}>
            <Input.TextArea rows={2} placeholder='{"amount":{"op":"gte","value":0}}' />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={2} maxLength={500} showCount />
          </Form.Item>

          <Typography.Title level={5}>审批节点</Typography.Title>
          <Form.List name="nodes">
            {(fields, { add, remove: removeNode }) => (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                {fields.map((field) => (
                  <Card
                    key={field.key}
                    size="small"
                    title={`节点 ${field.name + 1}`}
                    extra={<Button type="link" danger onClick={() => removeNode(field.name)}>删除</Button>}
                  >
                    <Space size={12} wrap>
                      <Form.Item
                        name={[field.name, 'nodeCode']}
                        label="编码"
                        rules={[{ required: true, message: '必填' }]}
                        style={{ width: 110 }}
                      >
                        <Input placeholder="N1" />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'nodeName']}
                        label="名称"
                        rules={[{ required: true, message: '必填' }]}
                        style={{ width: 180 }}
                      >
                        <Input placeholder="科室审批" />
                      </Form.Item>
                      <Form.Item name={[field.name, 'nodeType']} label="类型" style={{ width: 140 }}>
                        <Select options={NODE_TYPES} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'seq']} label="顺序" style={{ width: 90 }}>
                        <InputNumber min={1} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'approveScope']} label="审批人来源" style={{ width: 170 }}>
                        <Select
                          options={SCOPES}
                          onSelect={(value) => {
                            if (value === 'ROLE') form.setFieldValue(['nodes', field.name, 'roleCode'], ['PLATFORM_ADMIN']);
                          }}
                        />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'roleCode']}
                        label="角色"
                        style={{ width: 240 }}
                        dependencies={[['nodes', field.name, 'approveScope']]}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator: () => {
                              const scope = getFieldValue(['nodes', field.name, 'approveScope']);
                              const codes = getFieldValue(['nodes', field.name, 'roleCode']) as string[] | undefined;
                              // 只能是下拉里的真实角色：手填拼错的码后端解析不到人，节点会静默无人可批
                              if (scope === 'ROLE' && !codes?.length)
                                return Promise.reject(new Error('按角色审批必须选择角色'));
                              return Promise.resolve();
                            }
                          })
                        ]}
                      >
                        <Select
                          mode="multiple"
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder="选择角色（可多选）"
                          options={roleOptions}
                        />
                      </Form.Item>
                      <Form.Item name={[field.name, 'levelRequired']} label="账号等级" style={{ width: 160 }}>
                        <Select allowClear showSearch optionFilterProp="label" placeholder="不限等级" options={LEVEL_OPTIONS} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'userIds']} label="指定人员" style={{ width: 320 }}>
                        <UserMultiPicker hq={hq} nameMap={userNames} />
                      </Form.Item>
                      <Form.Item noStyle dependencies={[['nodes', field.name, 'approveScope']]}>
                        {({ getFieldValue }) => {
                          const scope = getFieldValue(['nodes', field.name, 'approveScope']) as string | undefined;
                          if (!scope || !DYNAMIC_SCOPES.includes(scope)) return null;
                          return (
                            <Form.Item
                              name={[field.name, 'resolveRuleJson']}
                              label="解析规则 JSON"
                              style={{ width: 620 }}
                              rules={[jsonRule('审批人解析规则')]}
                            >
                              <Input.TextArea rows={2} placeholder={DYNAMIC_SCOPE_PLACEHOLDERS[scope]} />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Space>
                    <Space size={12} wrap>
                      <Form.Item
                        name={[field.name, 'signRate']}
                        label="会签比例(%)"
                        tooltip="0 = 全员会签"
                        style={{ width: 120 }}
                      >
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'counterSignOrder']} label="会签顺序" style={{ width: 130 }}>
                        <Select
                          options={[
                            { value: 'SERIAL', label: '串行' },
                            { value: 'PARALLEL', label: '并行' }
                          ]}
                        />
                      </Form.Item>
                      <Form.Item name={[field.name, 'timeoutHours']} label="超时(小时)" style={{ width: 110 }}>
                        <InputNumber min={0} max={720} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'timeoutAction']} label="超时动作" style={{ width: 150 }}>
                        <Select options={TIMEOUT_ACTIONS} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'escalateLevelCode']} label="上抛等级" style={{ width: 160 }}>
                        <Select allowClear showSearch optionFilterProp="label" placeholder="不上抛" options={LEVEL_OPTIONS} />
                      </Form.Item>
                    </Space>
                  </Card>
                ))}
                <Button
                  block
                  type="dashed"
                  onClick={() =>
                    add({
                      nodeType: 'APPROVE',
                      seq: (fields.length + 1) * 10,
                      approveScope: 'DEPT_HEAD',
                      signRate: 100,
                      counterSignOrder: 'SERIAL',
                      timeoutHours: 24,
                      timeoutAction: 'REMIND'
                    })
                  }
                >
                  + 添加节点
                </Button>
              </Space>
            )}
          </Form.List>

          <Typography.Title level={5} style={{ marginTop: 24 }}>
            条件路由
          </Typography.Title>
          <Typography.Paragraph type="secondary">
            条件命中后向前跳到目标节点（跳过中间节点），写法：{'{'}&quot;amount&quot;:{'{'}&quot;op&quot;:&quot;gte&quot;,&quot;value&quot;:50000{'}'}
            {'}'}；多条件用数组。
          </Typography.Paragraph>
          <Form.List name="routes">
            {(fields, { add, remove: removeRoute }) => (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {fields.map((field) => (
                  <Space key={field.key} align="start" wrap>
                    <Form.Item
                      name={[field.name, 'conditionJson']}
                      label="条件 JSON"
                      style={{ width: 380 }}
                      rules={[jsonRule('条件路由表达式')]}
                    >
                      <Input.TextArea rows={2} placeholder='{"amount":{"op":"gte","value":20000}}' />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'targetNodeCode']}
                      label="目标节点"
                      rules={[{ required: true, message: '必选' }]}
                      style={{ width: 220 }}
                    >
                      <Select showSearch optionFilterProp="label" placeholder="选择本流程节点" options={nodeOptions} />
                    </Form.Item>
                    <Form.Item name={[field.name, 'priority']} label="优先级" style={{ width: 100 }}>
                      <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name={[field.name, 'description']} label="说明" style={{ width: 220 }}>
                      <Input />
                    </Form.Item>
                    <Button type="link" danger onClick={() => removeRoute(field.name)}>
                      删除
                    </Button>
                  </Space>
                ))}
                <Button type="dashed" block onClick={() => add({ priority: 0 })}>
                  + 添加路由
                </Button>
              </Space>
            )}
          </Form.List>
        </Form>
      </Drawer>
    </Card>
  );
}
